/**
 * ISATS Portal — encrypted-envelope guards (pure, React-free).
 *
 * Application PII fields are stored as { iv, ciphertext, authTag }
 * AES-256-GCM blocks and are decrypted ON THE SERVER before payloads
 * reach the client (P0-1a). Two leak shapes keep reaching UI code
 * anyway:
 *
 *  1. A read path returns stored envelopes untouched whenever
 *     server-side decryption is unavailable for a record (the pattern
 *     PR #1414 found in /api/admin/applicant-profiles). A component
 *     that renders the object crashes with React error #31; one that
 *     calls a string method on it throws a TypeError.
 *  2. A stored field is not a string at all (e.g. an emailTemplates
 *     `code` persisted as a Firestore integer by an out-of-band
 *     writer) and a pass-through read path delivers it verbatim to
 *     consumers that assume `string`.
 *
 * This module is the shared data-boundary guard against both shapes.
 * It is intentionally free of React imports so server code can use it
 * too (mirroring server/piiPayload.ts importing src/lib/piiFieldMap).
 * The display-facing helpers in src/lib/piiDisplay.ts build on it.
 */

export interface EncryptedPiiBlock {
  iv: string;
  ciphertext: string;
  authTag: string;
}

/** True when the value is an AES-GCM ciphertext block from encryptPiiBlock. */
export function isEncryptedPiiBlock(v: any): v is EncryptedPiiBlock {
  return (
    !!v &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    typeof (v as any).iv === 'string' &&
    typeof (v as any).ciphertext === 'string' &&
    typeof (v as any).authTag === 'string'
  );
}

/**
 * The safe display placeholder used across the portal when a value
 * cannot be shown (same fallback as safePiiText / PiiText in
 * piiDisplay.ts). An envelope replaced by this string can be rendered
 * and searched safely; it can never crash a component.
 */
export const PII_DISPLAY_PLACEHOLDER = '—';

const SANITIZE_MAX_DEPTH = 12;

function isPlainRecord(v: any): boolean {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

/**
 * Copy-on-write deep walk: every encrypted-envelope object found in
 * plain objects/arrays is replaced by `replacement`. Inputs are never
 * mutated; when no envelope is found the SAME reference is returned,
 * so memoized consumers do not re-render on unchanged data. Only
 * plain objects and arrays are walked (Date, Firestore Timestamp and
 * other class instances pass through untouched). Depth-capped:
 * anything deeper than the cap is returned as-is (Firestore payloads
 * are shallow JSON and never approach it).
 */
function replaceEnvelopesDeep(value: any, replacement: any, depth: number): any {
  if (depth > SANITIZE_MAX_DEPTH || value === null || value === undefined) return value;
  if (isEncryptedPiiBlock(value)) return replacement;
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((item) => {
      const next = replaceEnvelopesDeep(item, replacement, depth + 1);
      if (next !== item) changed = true;
      return next;
    });
    return changed ? out : value;
  }
  if (isPlainRecord(value)) {
    let changed = false;
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(value)) {
      const next = replaceEnvelopesDeep(v, replacement, depth + 1);
      if (next !== v) changed = true;
      out[k] = next;
    }
    return changed ? out : value;
  }
  return value;
}

/**
 * Deep envelope sanitizer for record families whose fields are
 * consumed as STRINGS (filters, badges, labels — templates,
 * automations, programs, …): every encrypted-envelope object anywhere
 * in the value is replaced by the safe display placeholder, so no
 * string-typed field can ever arrive at a component as an object.
 */
export function sanitizeEnvelopesDeep<T>(value: T, placeholder: string = PII_DISPLAY_PLACEHOLDER): T {
  return replaceEnvelopesDeep(value, placeholder, 0);
}

/**
 * Twin of sanitizeEnvelopesDeep that replaces envelopes with
 * `undefined`, so display surfaces fall back to their normal empty
 * states ('—', 'not provided', …). This is the behaviour
 * piiDisplay.sanitizeEncryptedPiiBlocks has always had for
 * application records.
 */
export function stripEnvelopesDeep<T>(value: T): T {
  return replaceEnvelopesDeep(value, undefined, 0);
}

/**
 * Coerce any value to a string that is always safe for search/filter
 * code: strings pass through, numbers and booleans stringify, and
 * anything else (objects, arrays, null, undefined) becomes '' — it
 * simply never matches a text search. String methods can always be
 * called on the result. Defence in depth for filter expressions over
 * record fields that are string-typed by contract but not by storage.
 */
export function searchText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/**
 * Boundary guard for template records (emailTemplates /
 * documentTemplates) delivered to UI consumers:
 *
 *  1. Every encrypted envelope anywhere in the record (including
 *     nested `versions[]` entries) is replaced by the display
 *     placeholder via sanitizeEnvelopesDeep.
 *  2. A present `code` is coerced to a string: a numeric/boolean code
 *     (e.g. a Firestore integer written by an out-of-band writer —
 *     the live OPS E31/E32 records) becomes its decimal string so
 *     search-by-code keeps working; any other non-string object code
 *     becomes the placeholder. A missing/null code is left alone.
 *
 * Never mutates the input; returns the same reference when the record
 * is already clean.
 */
export function sanitizeTemplateRecord<T>(record: T): T {
  const clean = sanitizeEnvelopesDeep(record);
  if (isPlainRecord(clean)) {
    const rec = clean as Record<string, any>;
    if ('code' in rec) {
      const code = rec.code;
      if (typeof code === 'number' || typeof code === 'boolean') {
        return { ...rec, code: String(code) } as T;
      }
      if (code !== null && code !== undefined && typeof code !== 'string') {
        return { ...rec, code: PII_DISPLAY_PLACEHOLDER } as T;
      }
    }
  }
  return clean;
}
